import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import { useCreateContext } from '../../contexts/CreateContext';
import EditIcon from "../../../src/assets/icons/EditIcon.svg";
import DeleteIcon from "../../../src/assets/icons/DeleteIcon.svg";
import ActionIcon from '../../../src/assets/icons/ActionIcon.svg';
import apiService, { parseGetAllUsersResponse } from '../../services/api';
import { DATE_FORMATS } from '../../constants';
import useHubs from '../../hooks/useHubs';
import { usePagination } from '../../hooks/usePagination';
import Pagination from '../../components/ui/Pagination';
import SortableTable, { SortableColumn } from '../../components/common/SortableTable';
import CreateStaffPage from './Create-Staff/CreateStaffPage';
import { SearchableSelectInput } from '../../components/forms';
import { useAppSelector } from '../../hooks/useAuth';
import useCodeCommonByCategory from '../../hooks/useCodeCommonByCategory';
import { formatMobileNumber } from '../../utils/formatters';
import {
  Paper,
  Chip,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import CustomLoading from '../../components/common/CustomLoading';

const useRoles = (setError: (msg: string | null) => void) => {
  const [roles, setRoles] = useState<Array<{ roleid: number; rolename: string }>>([]);
  const [loading, setLoading] = useState(false);

  const fetchRoles = useCallback(async () => {
    setLoading(true);
    try {
      const response = (await apiService.getAllRoles()) as any;
      setRoles(response.data || response || []);
    } catch (error) {
      setError("Failed to load roles. Please refresh the page.");
    } finally {
      setLoading(false);
    }
  }, [setError]);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  return { roles, loading };
};


interface StaffData {
  id: string;
  staffId: string;
  staffName: string;
  role: string;
  hubName: string;
  hubmasterid?: string | number;
  mobileNo: string;
  email: string;
  status: string;
  joiningDate: string;
}

const StatusChip = ({ status, statusCodes }: { status: string; statusCodes: Array<{ codedisplayname: string; codevalue: string }> }) => {
  // Find the status code to determine styling - all status data comes from API
  const statusCode = statusCodes.find(code => code.codedisplayname === status);
  
  // Default styles for unknown statuses
  const defaultStyle = { bg: "rgba(200, 200, 200, 1)", color: "rgba(100, 100, 100, 1)" };
  
  // Dynamic style mapping based on status code value from API
  // Styles are mapped to code values (which are consistent), not hardcoded display names
  const getStatusStyle = (codeValue: string | undefined) => {
    if (!codeValue) return defaultStyle;
    
    // Map code values to styles - this is based on code values from API, not hardcoded status names
    // Code values are consistent identifiers, while display names can change
    const styleMap: Record<string, { bg: string; color: string }> = {
      "1": { bg: "rgba(212, 255, 199, 1)", color: "rgba(31, 113, 13, 1)" },
      "2": { bg: "rgba(255, 227, 227, 1)", color: "rgba(197, 0, 0, 1)" },
    };
    
    return styleMap[codeValue] || defaultStyle;
  };
  
  const style = statusCode ? getStatusStyle(statusCode.codevalue) : defaultStyle;
  
  // Use the dynamic status display name from API, or empty string if not found
  const displayLabel = status || "";
  
  return (
    <Chip
      label={displayLabel}
      size="medium"
      sx={{ backgroundColor: style.bg, color: style.color, fontWeight: 600 }}
    />
  );
};

const StaffManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const getInitialSearchTerm = () => (location.state as any)?.searchTerm || '';
  const getInitialFilterData = () => {
    const state = location.state as any;
    return {
      role: state?.role || '',
      hub: state?.hub || '',
      staffstatus: state?.staffstatus || ''
    };
  };
  const getInitialPage = () => (location.state as any)?.page || 0;

  const [staffData, setStaffData] = useState<StaffData[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState(getInitialSearchTerm());
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [staffToDelete, setStaffToDelete] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [filterData, setFilterData] = useState(getInitialFilterData());
  const { setShowCreateStaff } = useCreateContext();
  const [rawStaffData, setRawStaffData] = useState<any[]>([]);

  useEffect(() => {
    const state = location.state as any;
    if (state) {
      setSearchTerm(state.searchTerm ?? '');
      setFilterData({
        role: state.role ?? '',
        hub: state.hub ?? '',
        staffstatus: state.staffstatus ?? '',
      });
    }
  }, [location.state]);

  const { roles, loading: rolesLoading } = useRoles(setError);
  const { statusOptions, loading: statusLoading, getDisplayNameByCodeValue, getCodeValueByDisplayName, statusCodes } = useCodeCommonByCategory('StaffStatus');
  const { hubOptions, loading: hubsLoading } = useHubs(setError);
  const { user } = useAppSelector((state) => state.auth);
  const isSuperAdmin = user?.roleid === 1;
  const hasEditDeletePermission = user?.roleid && [1, 2, 7, 10].includes(user.roleid);


  const handleFilterChange = (field: string, value: string) => {
    const newFilterData = { ...filterData, [field]: value };
    setFilterData(newFilterData);
    pagination.goToPage(0);
    navigate(location.pathname, {
      replace: true,
      state: {
        ...((location.state as object) || {}),
        ...newFilterData,
        page: 0,
      },
    });
  };

  const pagination = usePagination({
    totalItems: totalCount,
    initialPage: getInitialPage(),
    initialRowsPerPage: 10,
  });

  const handleRoleChange = (value: string) => {
    const newFilterData = { ...filterData, role: value };
    setFilterData(newFilterData);
    pagination.goToPage(0);
    navigate(location.pathname, {
      replace: true,
      state: {
        ...((location.state as object) || {}),
        ...newFilterData,
        page: 0,
      },
    });
  };

  const handleHubChange = (value: string) => {
    const newFilterData = { ...filterData, hub: value };
    setFilterData(newFilterData);
    pagination.goToPage(0);
    navigate(location.pathname, {
      replace: true,
      state: {
        ...((location.state as object) || {}),
        ...newFilterData,
        page: 0,
      },
    });
  };

  const roleOptions = useMemo(
    () => roles.map((r) => r.rolename).filter(Boolean).sort(),
    [roles]
  );
  const hubNameOptions = useMemo(
    () => hubOptions.map((h) => h.hubname).filter(Boolean).sort(),
    [hubOptions]
  );

  const displayedStaff = useMemo(() => {
    let list = staffData;
    if (user?.userid) {
      list = list.filter(
        (staff) => Number.parseInt(staff.staffId, 10) !== user.userid
      );
    }
    return list;
  }, [staffData, user?.userid]);

  const columns: SortableColumn<StaffData>[] = [
    { id: 'staffId', label: 'Staff ID' },
    { id: 'staffName', label: 'Staff Name' },
    { id: 'role', label: 'Role' },
    { id: 'hubName', label: 'Hub Name' },
    { id: 'mobileNo', label: 'Mobile No.' },
    { id: 'email', label: 'Email' },
    { 
      id: 'status', 
      label: 'Status', 
      align: 'center',
      render: (row: StaffData) => <StatusChip status={row.status} statusCodes={statusCodes} />
    },
    { id: 'joiningDate', label: 'Joining Date' },
    {
      id: 'actions' as keyof StaffData,
      label: 'Actions',
      align: 'center',
      sortable: false,
      render: (row: StaffData) => (
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
          <Button 
            variant="text" 
            sx={{ p: 0, minWidth: "auto" }}
            onClick={() => handleViewStaffDetails(row.id)}
          >
            <img src={ActionIcon} alt="Action" width={30} height={30} />
          </Button>
          {hasEditDeletePermission && (
            <Button 
              variant="text" 
              sx={{ p: 0, minWidth: "auto" }}
              onClick={() => handleEditStaff(row.id)}
            >
              <img src={EditIcon} alt="Edit" width={30} height={30} />
            </Button>
          )}
          {hasEditDeletePermission && (
            <Button 
              variant="text" 
              sx={{ p: 0, minWidth: "auto" }}
              onClick={() => handleDeleteStaff(row.id)}
            >
              <img src={DeleteIcon} alt="Delete" width={30} height={30} />
            </Button>
          )}
        </Box>
      )
    },
  ];

  const fetchStaffData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const roleId = filterData.role
        ? roles.find((r) => r.rolename === filterData.role)?.roleid ?? null
        : null;

      let hubId: number | null = null;
      if (isSuperAdmin) {
        if (filterData.hub) {
          const h = hubOptions.find((x) => x.hubname === filterData.hub);
          hubId = h?.hubmasterid ?? null;
        }
      } else {
        const hid = (user as { hubid?: number } | null)?.hubid;
        hubId = hid != null && hid !== 0 ? hid : null;
      }

      let userstatus: number | null = null;
      if (filterData.staffstatus) {
        const cv = getCodeValueByDisplayName(filterData.staffstatus);
        if (cv != null && cv !== "") {
          const n = Number.parseInt(cv, 10);
          userstatus = Number.isFinite(n) ? n : null;
        }
      }

      const payload = {
        name: searchTerm.trim() || null,
        roleid: roleId,
        hubid: hubId,
        userstatus,
        kycstatus: null,
        registrationdate: null as string | null,
        reporttype: "staff",
        pageNumber: pagination.page + 1,
        pageSize: pagination.rowsPerPage,
      };

      const response = await apiService.getAllUsers(payload);
      const { items, total } = parseGetAllUsersResponse(response);
      setRawStaffData(Array.isArray(items) ? items : []);
      setTotalCount(total);
    } catch (err: any) {
      setError(err.message || "Failed to fetch staff");
      setRawStaffData([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [
    filterData.role,
    filterData.hub,
    filterData.staffstatus,
    searchTerm,
    roles,
    hubOptions,
    isSuperAdmin,
    user,
    pagination.page,
    pagination.rowsPerPage,
    getCodeValueByDisplayName,
  ]);

  useEffect(() => {
    const shouldRefresh = location.state && (location.state as any).refresh;
    if (shouldRefresh) {
      fetchStaffData();
      navigate(location.pathname, { replace: true, state: {} });
    } else {
      fetchStaffData();
    }
  }, [fetchStaffData, navigate, location.state]);

  const transformStaffData = useCallback((staff: any): StaffData => {
    // Get status display name from codevalue using the status codes
    const statusDisplayName = getDisplayNameByCodeValue(staff?.staffstatus) || "";

    return {
      id: `${staff.userid}-${staff.roleid}`,
      staffId: staff.userid.toString(),
      staffName: staff.name || "",
      role: staff.rolename || "",
      hubName: staff.hubname || "N/A",
      hubmasterid: staff.hubid,
      mobileNo: formatMobileNumber(staff.phonenumber) || "",
      email: staff.emailid || "",
      status: statusDisplayName,
      joiningDate: DATE_FORMATS.formatDate(staff.joineddate || "")
    };
  }, [getDisplayNameByCodeValue]);

  useEffect(() => {
    if (statusOptions.length === 0) return;

    const transformedStaff = rawStaffData.map(transformStaffData);
    setStaffData(transformedStaff);
  }, [rawStaffData, statusOptions, transformStaffData]);

  const handleEditStaff = (staffId: string) => {
    const originalStaffId = staffId.split('-')[0];
    navigate(`/staff-management/edit-staff/${originalStaffId}`, {
      state: {
        searchTerm,
        role: filterData.role,
        hub: filterData.hub,
        staffstatus: filterData.staffstatus,
        page: pagination.page,
      },
    });
  };

  const handleViewStaffDetails = (staffId: string) => {
    const originalStaffId = staffId.split('-')[0];
    navigate(`/staff-management/staff-details/${originalStaffId}`, {
      state: {
        searchTerm,
        role: filterData.role,
        hub: filterData.hub,
        staffstatus: filterData.staffstatus,
        page: pagination.page,
      },
    });
  };

  const handleDeleteStaff = (staffId: string) => {
    const originalStaffId = staffId.split('-')[0];
    setStaffToDelete(originalStaffId);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteStaff = async () => {
    if (!staffToDelete) return;

    try {
      setDeleting(true);
      setError(null);
      
      await apiService.deleteStaff(staffToDelete);
      
      setDeleteDialogOpen(false);
      setStaffToDelete(null);
      fetchStaffData();
      
    } catch (err: any) {
      setError(err.message || "Failed to delete staff");
    } finally {
      setDeleting(false);
    }
  };

  const cancelDelete = () => {
    setDeleteDialogOpen(false);
    setStaffToDelete(null);
  };

  if (location.pathname === "/staff-management/create-staff") {
    return <CreateStaffPage onBack={() => setShowCreateStaff(false)} />;
  }

  return (
    <>
    <Container fluid className="px-0">
      {error && (
        <Alert 
          severity="error" 
          className="mb-3" 
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      <Paper className="mb-3 p-3 border-0 rounded-0 shadow-none">
        <Row className="g-3 align-items-end">
          <Col xs={12} md={2}>
            <TextField
              label="Staff Name"
              placeholder="Enter staff name"
              size="small"
              fullWidth
              value={searchTerm}
              onChange={(e) => {
                const newSearchTerm = e.target.value;
                setSearchTerm(newSearchTerm);
                pagination.goToPage(0);
                navigate(location.pathname, {
                  replace: true,
                  state: {
                    ...((location.state as object) || {}),
                    searchTerm: newSearchTerm,
                    page: 0,
                  },
                });
              }}
            />
          </Col>

          <Col xs={12} md={2}>
            <SearchableSelectInput
              field="role"
              formData={filterData}
              onChange={(_, value) => handleRoleChange(value as string)}
              label="Role"
              disabled={rolesLoading}
              placeholder="Search and select role"
              options={roleOptions}
            />
          </Col>

          {isSuperAdmin && (
            <Col xs={12} md={2}>
              <SearchableSelectInput
                field="hub"
                formData={filterData}
                onChange={(_, value) => handleHubChange(value as string)}
                label="Hub"
                disabled={hubsLoading}
                placeholder="Search and select hub"
                options={hubNameOptions}
              />
            </Col>
          )}

          <Col xs={12} md={2}>
            <SearchableSelectInput
              field="staffstatus"
              formData={filterData}
              onChange={handleFilterChange}
              label="Status"
              disabled={statusLoading}
              placeholder="Search and select status"
              options={statusOptions}
            />
          </Col>
        </Row>
      </Paper>

        <Paper className="border-0 rounded-0 shadow-none">
          {loading ? (
            <CustomLoading message="Fetching Staff Table Data..." />
          ) : (
            <SortableTable
              data={displayedStaff}
              columns={columns}
              emptyMessage="No staff found"
            />
          )}
        </Paper>
    </Container>
      <Pagination
        page={pagination.page}
        rowsPerPage={pagination.rowsPerPage}
        totalItems={totalCount}
        totalPages={pagination.totalPages}
        startItem={pagination.startItem}
        endItem={pagination.endItem}
        rowsPerPageOptions={[5, 10, 25, 50]}
        showRowsPerPage
        onRowsPerPageChange={pagination.handleChangeRowsPerPage}
        onGoToNextPage={pagination.goToNextPage}
        onGoToPreviousPage={pagination.goToPreviousPage}
      />

      <Dialog
        open={deleteDialogOpen}
        onClose={cancelDelete}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete this staff member? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={cancelDelete} 
            disabled={deleting}
            sx={{ color: "rgba(13, 44, 90, 1)" }}
          >
            Cancel
          </Button>
          <Button 
            onClick={confirmDeleteStaff} 
            disabled={deleting}
            variant="contained"
            sx={{ 
              backgroundColor: "rgba(197, 0, 0, 1)",
              "&:hover": { backgroundColor: "rgba(197, 0, 0, 0.8)" }
            }}
          >
            {deleting ? <CircularProgress size={20} color="inherit" /> : "Yes, Delete"}
          </Button>
        </DialogActions>
      </Dialog>
  
   </>
   );
};

export default StaffManagementPage;
